import { Formik, Form, FormikProps } from 'formik';
import * as Yup from 'yup';
import Button from '../../../components/buttons/Button';
import { useRef, useState } from 'react';
import { scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import useModelSelectOptions from '../../../hooks/useModelSelectOptions';
import CreateOrderFormInputs from './CreateOrderFormInputs';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';
import CreateOrderLead from './CreateOrderLead';
import CreateOrderFormDeliveryInputs from './CreateOrderFormDeliveryInputs';
import { useSelector } from 'react-redux';
import ModalContainer from '../../../components/ModalContainer';
import ConfirmModal from '../../../components/ConfirmModal';

const CreateOrderForm = () => {
  const { id } = useParams();
  const formRef = useRef(null);
  const formikRef = useRef<FormikProps<any> | null>(null);
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState<number>(id ? 2 : 1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const authData = useSelector((state: any) => state.authData);

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    id,
    singleGetApi: `/admin/orders/${id}`,
    invalidateQueryKey: ['orders'],
    queryKey: ['orders', id],
    addApi: `/admin/orders`,
    editApi: `/admin/orders/${id}`,
    listRoute: '/orders',
    itemName: t('order'),
    keysToKeepEmpty: ['warranty_id']
  });

  const { apiData: fullWarrantiesData } = useModelSelectOptions({
    labelKey: [`title[${i18n.language}]`],
    valueKey: 'id',
    modelRoute: `admin/warranties`,
    queryKey: ['warranties']
  });

  const validationSchema = Yup.object({
    chassis: Yup.string().required(t('required')),
    ...(!id
      ? {
          sales_id: Yup.string().required(t('required')),
          first_name: Yup.string().required(t('required')),
          last_name: Yup.string().required(t('required')),
          email: Yup.string().required(t('required')),
          mobile: Yup.string().required(t('required')),
          national_id: Yup.string().required(t('required'))
        }
      : {}),
    delivery_type: Yup.string().required(t('required')),
    city_id: Yup.string().required(t('required')),
    delivery_option_id: Yup.string().when('delivery_type', {
      is: 'Home Delivery',
      then: (schema) => schema.required(t('required')),
      otherwise: (schema) => schema.notRequired()
    }),
    showroom_id: Yup.string().when('delivery_type', {
      is: 'Showroom',
      then: (schema) => schema.required(t('required')),
      otherwise: (schema) => schema.notRequired()
    }),
    area: Yup.string().when('delivery_type', {
      is: 'Home Delivery',
      then: (schema) => schema.required(t('required')),
      otherwise: (schema) => schema.notRequired()
    }),
    street: Yup.string().when('delivery_type', {
      is: 'Home Delivery',
      then: (schema) => schema.required(t('required')),
      otherwise: (schema) => schema.notRequired()
    }),
    building_no: Yup.string().when('delivery_type', {
      is: 'Home Delivery',
      then: (schema) => schema.required(t('required')),
      otherwise: (schema) => schema.notRequired()
    }),
    apartment_no: Yup.string().when('delivery_type', {
      is: 'Home Delivery',
      then: (schema) => schema.required(t('required')),
      otherwise: (schema) => schema.notRequired()
    })
  });

  const initialValues = {
    ...apiData,

    sales_id: authData?.userData?.type === 'Sales' ? authData?.userData?.id : '',

    first_name: apiData?.user?.first_name || '',
    last_name: apiData?.user?.last_name || '',
    email: apiData?.user?.email || '',
    mobile: apiData?.user?.mobile || '',
    national_id: apiData?.user?.national_id || '',
    lead_id: apiData?.user?.lead_id || '',

    chassis: apiData?.vehicle?.chassis || '',
    vehicle_id: apiData?.vehicle?.id || '',
    interior_color_id: apiData?.vehicle?.interior_color?.id || '',
    exterior_color_id: apiData?.vehicle?.exterior_color?.id || '',
    variant_id: apiData?.vehicle?.variant?.id || '',
    model_id: apiData?.vehicle?.variant?.vehicle_model?.id || '',
    make_id: apiData?.vehicle?.variant?.vehicle_model?.vehicle_make?.id || '',
    warranty_id: apiData?.warranty?.id || '',
    showroom_id: apiData?.showroom?.id || '',
    delivery_type: apiData?.showroom ? 'showroom' : apiData?.additional_details?.delivery_option ? 'home-delivery' : '',
    city_id: apiData?.city?.id || apiData?.showroom?.city?.id || '',
    delivery_option_id: Number(apiData?.additional_details?.delivery_option_id) || '',
    area: apiData?.address?.split(',')[0].trim() || '',
    street: apiData?.address?.split(',')[1].trim() || '',
    building_no: apiData?.address?.split(',')[2].trim() || '',
    apartment_no: apiData?.address?.split(',')[3].trim() || '',

    accessories: apiData?.accessories?.map((ele: any) => String(ele.id)) || [],
    manual_chassis: 1,
    ...(!id ? { sales_id: authData?.userData?.id || '' } : {})
  };

  if (getDataLoading)
    return (
      <FormDynamicSkeleton
        buttons={1}
        sections={[
          [
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 }
          ]
        ]}
      />
    );

  return (
    <div className={'form_section, create_order'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setErrors }) => {
          let value = { ...values, _method: 'POST' };
          delete value.vehicle;
          if (values?.delivery_type === 'home-delivery') {
            delete value.showroom_id;
            value.address = [values?.area?.replaceAll(',', ' ') || '', values?.street?.replaceAll(',', ' ') || '', values?.building_no || '', values?.apartment_no || '']?.join(
              ','
            );
          } else if (values?.delivery_type === 'showroom') {
            delete value.city_id;
            delete value.delivery_option_id;
            delete value.address;
          }

          delete value.order_value;

          if (fullWarrantiesData?.find((item: any) => item.id === values?.warranty_id)?.is_default) {
            value.warranty_id = '';
          }

          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value }, setErrors);
        }}
      >
        {(formik) => {
          return (
            <Form className="form_wrapper">
              <CreateOrderLead expanded={expanded} setExpanded={setExpanded} />
              <CreateOrderFormInputs formik={formik} expanded={expanded} setExpanded={setExpanded} currentOrderData={apiData} />
              <CreateOrderFormDeliveryInputs formik={formik} expanded={expanded} setExpanded={setExpanded} />

              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  disabled={submitLoading}
                  type="button"
                  onClick={() => {
                    // Check chassis stock validation before showing confirm modal
                    if (formik.values.manual_chassis === 1 && !formik.values.vehicle_id) {
                      formik.setFieldError('chassis', t('chassis_stock_required'));
                      scrollToError(true, formRef);
                      return;
                    }
                    if (id) {
                      formik.handleSubmit();
                    } else {
                      formikRef.current = formik;

                      if (!formik.isValid) {
                        formik.handleSubmit();
                        scrollToError(true, formRef);
                      } else {
                        setShowConfirmModal(true);
                      }
                    }
                  }}
                >
                  <span className="bold">{id ? t('update_order') : t('create_order')}</span>
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>

      {showConfirmModal && (
        <ModalContainer small>
          <ConfirmModal
            closeModal={() => setShowConfirmModal(false)}
            message={t('are_you_sure_you_want_to_create_this_order')}
            successMsg={t('order_created_successfully')}
            confirmFunc={() => {
              if (formikRef.current) {
                formikRef.current.handleSubmit();
              }
            }}
          />
        </ModalContainer>
      )}
    </div>
  );
};

export default CreateOrderForm;
